/**
 * Feed Visibility Hook
 *
 * Fires `<entityType>_read` after a feed item has been ≥50% visible for 5s.
 * Only attach to items in scrollable lists — $pageview covers full-page views.
 * Fires at most once per entity per page session.
 */

import { useEffect, useRef } from 'react';
import { entity_read } from './events';

export interface UseTrackEntityVisibilityOptions {
  entityType: string;
  entityId: string;
  parentEntityType?: string;
  parentEntityId?: string;
  locationPostedIn?: string;
  locationGroupId?: string;
  locationEventId?: string;
  locationOrganizationId?: string;
  postType?: string;
  contentType?: string;
  contentText?: string;
  disabled?: boolean;
}

const READ_ENTITY_TYPES = new Set([
  'post',
  'comment',
  'poll',
  'question',
  'answer',
  'article',
  'announcement',
]);

const READ_COOLDOWN_MS = 30 * 60 * 1000;
const lastReadAt = new Map<string, number>();
const seenEntities = new Set<string>();

const observerRegistry = new Map<string, IntersectionObserver>();
function getObserver(threshold: number, cb: IntersectionObserverCallback): IntersectionObserver {
  const key = String(threshold);
  if (!observerRegistry.has(key)) {
    observerRegistry.set(key, new IntersectionObserver(cb, { threshold }));
  }
  return observerRegistry.get(key)!;
}

const timers = new Map<Element, ReturnType<typeof setTimeout>>();
const visibleSince = new Map<Element, number>();
const maxIntersectionRatio = new Map<Element, number>();
const interactionCounts = new Map<Element, number>();
let interactionListenerCount = 0;

function getPageScrollDepth(): number {
  if (typeof window === 'undefined') return 0;

  const documentHeight = document.documentElement.scrollHeight;
  if (documentHeight <= window.innerHeight) return 100;

  const scrollTop = window.scrollY || document.documentElement.scrollTop;
  return Math.min(100, Math.round(((scrollTop + window.innerHeight) / documentHeight) * 100));
}

function incrementInteraction(event: Event): void {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const analyticsElement = target.closest('[data-analytics-entity-id]');
  if (!analyticsElement) return;

  interactionCounts.set(
    analyticsElement,
    (interactionCounts.get(analyticsElement) ?? 0) + 1
  );
}

function attachInteractionListeners(): void {
  if (interactionListenerCount === 0) {
    document.addEventListener('click', incrementInteraction, true);
    document.addEventListener('keydown', incrementInteraction, true);
  }
  interactionListenerCount += 1;
}

function detachInteractionListeners(): void {
  interactionListenerCount = Math.max(0, interactionListenerCount - 1);
  if (interactionListenerCount === 0) {
    document.removeEventListener('click', incrementInteraction, true);
    document.removeEventListener('keydown', incrementInteraction, true);
  }
}

function handleIntersection(entries: IntersectionObserverEntry[]) {
  for (const entry of entries) {
    const el = entry.target;
    const entityType = el.getAttribute('data-analytics-entity-type') ?? '';
    const entityId = el.getAttribute('data-analytics-entity-id') ?? '';

    if (!entityType || !entityId) continue;

    if (entry.intersectionRatio >= 0.5) {
      visibleSince.set(el, visibleSince.get(el) ?? Date.now());
      maxIntersectionRatio.set(
        el,
        Math.max(maxIntersectionRatio.get(el) ?? 0, entry.intersectionRatio)
      );
      if (timers.has(el)) continue;

      timers.set(el, setTimeout(() => {
        timers.delete(el);
        if (!READ_ENTITY_TYPES.has(entityType)) return;

        const entityKey = `${entityType}:${entityId}`;
        const now = Date.now();
        const previousReadAt = lastReadAt.get(entityKey) ?? 0;
        if (now - previousReadAt < READ_COOLDOWN_MS) return;
        lastReadAt.set(entityKey, now);

        const parentEntityType = el.getAttribute('data-analytics-parent-entity-type') ?? undefined;
        const parentEntityId = el.getAttribute('data-analytics-parent-entity-id') ?? undefined;
        const locationPostedIn = el.getAttribute('data-analytics-location-posted-in') ?? undefined;
        const locationGroupId = el.getAttribute('data-analytics-location-group-id') ?? undefined;
        const locationEventId = el.getAttribute('data-analytics-location-event-id') ?? undefined;
        const locationOrganizationId = el.getAttribute('data-analytics-location-organization-id') ?? undefined;
        const postType = el.getAttribute('data-analytics-post-type') ?? undefined;
        const contentType = el.getAttribute('data-analytics-content-type') ?? undefined;
        const contentText = el.getAttribute('data-analytics-content-text') ?? undefined;
        const viewDuration = Math.max(
          5,
          Math.round((Date.now() - (visibleSince.get(el) ?? Date.now())) / 1000)
        );
        const scrollDepth = getPageScrollDepth();

        entity_read({
          entity_type: entityType,
          entity_id: entityId,
          parent_type: parentEntityType,
          parent_id: parentEntityId,
          location_posted_in: locationPostedIn,
          location_group_id: locationGroupId,
          location_event_id: locationEventId,
          location_organization_id: locationOrganizationId,
          post_type: postType,
          content_type: contentType,
          content_text: contentText,
          engagement_view_duration: viewDuration,
          engagement_scroll_depth: scrollDepth,
          engagement_completion_rate: Math.round((maxIntersectionRatio.get(el) ?? entry.intersectionRatio) * 100),
          engagement_scrolled_to_end: scrollDepth >= 90,
          engagement_returned_to_content: seenEntities.has(entityKey),
          engagement_interaction_count: interactionCounts.get(el) ?? 0,
        });
        seenEntities.add(entityKey);
      }, 5_000));
    } else {
      const t = timers.get(el);
      if (t !== undefined) { clearTimeout(t); timers.delete(el); }
      visibleSince.delete(el);
    }
  }
}

export function useTrackEntityVisibility(
  options: UseTrackEntityVisibilityOptions,
): React.RefObject<HTMLElement> {
  const {
    entityType,
    entityId,
    parentEntityType,
    parentEntityId,
    locationPostedIn,
    locationGroupId,
    locationEventId,
    locationOrganizationId,
    postType,
    contentType,
    contentText,
    disabled,
  } = options;
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    if (disabled) return;

    const el = ref.current;
    if (!el) return;

    attachInteractionListeners();

    el.setAttribute('data-analytics-entity-type', entityType);
    el.setAttribute('data-analytics-entity-id', entityId);
    if (parentEntityType) el.setAttribute('data-analytics-parent-entity-type', parentEntityType);
    if (parentEntityId) el.setAttribute('data-analytics-parent-entity-id', parentEntityId);
    if (locationPostedIn) el.setAttribute('data-analytics-location-posted-in', locationPostedIn);
    if (locationGroupId) el.setAttribute('data-analytics-location-group-id', locationGroupId);
    if (locationEventId) el.setAttribute('data-analytics-location-event-id', locationEventId);
    if (locationOrganizationId) el.setAttribute('data-analytics-location-organization-id', locationOrganizationId);
    if (postType) el.setAttribute('data-analytics-post-type', postType);
    if (contentType) el.setAttribute('data-analytics-content-type', contentType);
    if (contentText) el.setAttribute('data-analytics-content-text', contentText);

    const observer = getObserver(0.5, handleIntersection);
    observer.observe(el);

    return () => {
      observer.unobserve(el);
      const t = timers.get(el);
      if (t !== undefined) { clearTimeout(t); timers.delete(el); }
      visibleSince.delete(el);
      maxIntersectionRatio.delete(el);
      interactionCounts.delete(el);
      el.removeAttribute('data-analytics-entity-type');
      el.removeAttribute('data-analytics-entity-id');
      el.removeAttribute('data-analytics-parent-entity-type');
      el.removeAttribute('data-analytics-parent-entity-id');
      el.removeAttribute('data-analytics-location-posted-in');
      el.removeAttribute('data-analytics-location-group-id');
      el.removeAttribute('data-analytics-location-event-id');
      el.removeAttribute('data-analytics-location-organization-id');
      el.removeAttribute('data-analytics-post-type');
      el.removeAttribute('data-analytics-content-type');
      el.removeAttribute('data-analytics-content-text');
      detachInteractionListeners();
    };
  }, [
    entityType,
    entityId,
    parentEntityType,
    parentEntityId,
    locationPostedIn,
    locationGroupId,
    locationEventId,
    locationOrganizationId,
    postType,
    contentType,
    contentText,
    disabled,
  ]);

  return ref;
}
